import React, { useEffect, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { EyeIcon, EyeOffIcon, ShieldCheck, Award, Briefcase, Building2, Globe } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { login } from '../store/slices/authSlice'
import { useNavigate } from 'react-router-dom'
import LOGO from '../assets/Logo/Logo.png'
import { useSnackbar } from 'notistack'

const LoginSchema = Yup.object().shape({
  username: Yup.string().required('Username is required'),
  password: Yup.string().required('Password is required')
})

const Login = () => {
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [showPassword, setShowPassword] = useState(false)
  const { isAuthenticated } = useSelector(state => state.auth)
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    const handleMouseMove = e => {
      const x = (e.clientX / window.innerWidth - 0.5) * 20
      const y = (e.clientY / window.innerHeight - 0.5) * 20
      setOffset({ x, y })
    }
    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])

  return (
    <div className='h-screen flex flex-col overflow-y-auto lg:overflow-hidden relative bg-[#1a1f2e]'>
      {/* Dynamic Background */}
      <div
        className='absolute inset-0 z-0 opacity-40'
        style={{
          backgroundImage: `
              url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100%25' height='100%25'%3E%3Crect fill='%231A1F2E' width='540' height='450'/%3E%3Cdefs%3E%3ClinearGradient id='a' gradientUnits='userSpaceOnUse' x1='0' x2='0' y1='0' y2='100%25' gradientTransform='rotate(240)'%3E%3Cstop offset='0' stop-color='%231A1F2E'/%3E%3Cstop offset='1' stop-color='%23373F5A'/%3E%3C/linearGradient%3E%3Cpattern patternUnits='userSpaceOnUse' id='b' width='300' height='250' x='0' y='0' viewBox='0 0 1080 900'%3E%3Cg fill-opacity='0.1'%3E%3Cpolygon fill='%23444' points='90 150 0 300 180 300'/%3E%3Cpolygon points='90 150 180 0 0 0'/%3E%3Cpolygon fill='%23AAA' points='270 150 360 0 180 0'/%3E%3Cpolygon fill='%23DDD' points='450 150 360 300 540 300'/%3E%3Cpolygon fill='%23999' points='450 150 540 0 360 0'/%3E%3C/g%3E%3C/pattern%3E%3C/defs%3E%3Crect x='0' y='0' fill='url(%23a)' width='100%25' height='100%25'/%3E%3Crect x='0' y='0' fill='url(%23b)' width='100%25' height='100%25'/%3E%3C/svg%3E")
            `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: `translate(${offset.x}px, ${offset.y}px)`,
          transition: 'transform 0.4s ease-out'
        }}
      />

      <div className='flex-1 w-full max-w-7xl mx-auto px-4 relative z-10 flex flex-col lg:flex-row gap-4 lg:gap-8 items-center justify-center'>

        {/* Left Side: Management & Information */}
        <div className='w-full lg:w-1/2 space-y-4 animate-in fade-in slide-in-from-left duration-700 order-2 lg:order-1'>
          <div className="space-y-2">
            <h2 className="text-3xl md:text-4xl font-black text-white leading-tight">
              Leading with <span className="text-[#e8bd79]">Vision</span>,<br />
              Building with <span className="text-[#e8bd79]">Trust</span>.
            </h2>
            <div className="h-1 w-24 bg-gradient-to-r from-[#e8bd79] to-transparent rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Chairman Card */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 p-4 rounded-[1.5rem] hover:bg-white/10 transition-all group shadow-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-amber-500/10 rounded-xl border border-amber-500/20">
                  <Award className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h4 className="text-[#e8bd79] text-[9px] font-black uppercase tracking-widest">Chairman</h4>
                  <h3 className="text-white text-base font-bold">Safdar Abbas</h3>
                </div>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">
                "Pioneering excellence and steering Al Noor Developers towards a brighter, more sustainable urban future."
              </p>
            </div>

            {/* Chief Executive Card */}
            <div className="bg-white/5 backdrop-blur-md border border-white/10 p-4 rounded-[1.5rem] hover:bg-white/10 transition-all group shadow-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                  <ShieldCheck className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-emerald-400 text-[9px] font-black uppercase tracking-widest">Chief Executive</h4>
                  <h3 className="text-white text-base font-bold">M. Manzar Abbas</h3>
                </div>
              </div>
              <p className="text-slate-400 text-xs leading-relaxed opacity-80 group-hover:opacity-100 transition-opacity">
                "Dedicated to operational integrity and ensuring every project reflects our commitment to premium quality."
              </p>
            </div>
          </div>

          {/* Development Section */}
          <div className="bg-gradient-to-br from-white/5 to-transparent backdrop-blur-md border border-white/10 p-5 rounded-[2rem]">
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-6 h-6 text-[#e8bd79]" />
              <h3 className="text-xl font-black text-white uppercase tracking-tight">Trust & Development</h3>
            </div>
            <div className="space-y-3 text-slate-300">
              <div className="flex gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#e8bd79] shrink-0" />
                <p className="text-xs font-medium">Standardizing modern living through state-of-the-art infrastructure and intentional society development.</p>
              </div>
              <div className="flex gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#e8bd79] shrink-0" />
                <p className="text-xs font-medium">Fostering a culture of transparency and reliability in every real estate transaction.</p>
              </div>
              <div className="flex gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#e8bd79] shrink-0" />
                <p className="text-xs font-medium">Committed to environmental sustainability and community-focused urban planning.</p>
              </div>
            </div>
            <div className="mt-6 flex gap-6 opacity-50">
              <div className="flex items-center gap-2">
                <Briefcase className="w-3 h-3" />
                <span className="text-[9px] font-black uppercase tracking-widest">Global Standards</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-3 h-3" />
                <span className="text-[9px] font-black uppercase tracking-widest">Local Heritage</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className='w-full lg:w-[380px] animate-in fade-in slide-in-from-right duration-700 order-1 lg:order-2'>
          <div
            className='px-6 py-6 md:p-8 rounded-[2.5rem] border border-[#e8bd79]/30 shadow-2xl relative'
            style={{
              background: `linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(30, 41, 59, 0.95))`
            }}
          >
            <div className='flex flex-col items-center mb-6'>
              <img src={LOGO} alt='Logo' className='w-32 mb-4 drop-shadow-2xl' />
              <div className="text-center">
                <h1 className="text-white text-lg font-black uppercase tracking-[0.2em]">Authorized Access</h1>
                <p className="text-slate-500 text-[9px] font-bold uppercase tracking-widest mt-1">Management Information System</p>
              </div>
            </div>

            <Formik
              initialValues={{ username: '', password: '' }}
              validationSchema={LoginSchema}
              onSubmit={async (values, { setSubmitting }) => {
                try {
                  const result = await dispatch(login(values)).unwrap()
                  if (result) {
                    enqueueSnackbar('Welcome back!', { variant: 'success' })
                    navigate('/')
                  }
                } catch (err) {
                  console.error('Login failed:', err)
                  const message = err?.message || 'Invalid credentials'
                  enqueueSnackbar(message, { variant: 'error' })
                } finally {
                  setSubmitting(false)
                }
              }}
            >
              {({ errors, touched, isSubmitting }) => (
                <Form className='space-y-6'>
                  <div className='space-y-2'>
                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Username</label>
                    <Field
                      name='username'
                      type='text'
                      placeholder='ENTER USERNAME'
                      className='w-full px-6 py-4 rounded-2xl border border-white/10 bg-white/5 text-white text-xs font-bold tracking-wider focus:outline-none focus:ring-2 focus:ring-[#e8bd79]/50 transition-all placeholder:text-slate-600'
                    />
                    {errors.username && touched.username && (
                      <div className='text-rose-500 text-[10px] font-black uppercase tracking-widest mt-1 ml-1'>{errors.username}</div>
                    )}
                  </div>

                  <div className='space-y-2 relative'>
                    <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest ml-1">Password</label>
                    <div className="relative">
                      <Field
                        name='password'
                        type={showPassword ? 'text' : 'password'}
                        placeholder='••••••••'
                        className='w-full px-6 py-4 rounded-2xl border border-white/10 bg-white/5 text-white text-xs font-bold tracking-wider focus:outline-none focus:ring-2 focus:ring-[#e8bd79]/50 transition-all placeholder:text-slate-600'
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className='absolute right-5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors'
                      >
                        {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                      </button>
                    </div>
                    {errors.password && touched.password && (
                      <div className='text-rose-500 text-[10px] font-black uppercase tracking-widest mt-1 ml-1'>{errors.password}</div>
                    )}
                  </div>

                  <div className='flex items-center justify-between px-1'>
                    <label className='flex items-center cursor-pointer group'>
                      <Field type='checkbox' name='rememberMe' className='w-4 h-4 rounded border-white/10 bg-white/5 text-[#e8bd79] focus:ring-[#e8bd79]' />
                      <span className='ml-3 text-[10px] font-black text-slate-400 group-hover:text-white uppercase transition-colors'>Remember?</span>
                    </label>
                    <a href='#' className='text-[#e8bd79] text-[10px] font-black uppercase tracking-widest hover:underline decoration-2 underline-offset-4'>
                      Forgot Access?
                    </a>
                  </div>

                  <button
                    type='submit'
                    disabled={isSubmitting}
                    className='w-full bg-[#e8bd79] hover:bg-[#d1cdb5] text-[#1a1f2e] py-5 rounded-2xl font-black uppercase tracking-[0.2em] transition-all duration-300 shadow-xl shadow-amber-500/10 active:scale-95 disabled:opacity-50'
                  >
                    {isSubmitting ? 'Verifying...' : 'Authenticate'}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 pb-6 opacity-70 hover:opacity-100 transition-opacity w-full flex justify-center">
        <a
          href="https://autorobos.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#e8bd79] text-[10px] font-black uppercase tracking-[0.2em] flex items-center gap-2 hover:underline decoration-2 underline-offset-4"
        >
          <span>Powered & Developed by</span>
          <span className="bg-white/10 px-2 py-1 rounded-md border border-white/10">Autorobos</span>
        </a>
      </div>
    </div>
  )
}

export default Login
